/**
 * currentTimeTool.ts
 *
 * Platform tool that returns current time (optional city/timezone).
 * Used for "What time is it?" / "What time is it in [city]?" so the
 * response can show a time annotation card.
 */

import type { PlatformTool, ToolFunction } from '@/types/platformTools'
import type { CurrentTimeResult } from '@/types/platformTools'

// Minimal city name -> IANA timezone for common queries (e.g. empty chat widget)
const CITY_TIMEZONE: Record<string, string> = {
  'king city': 'America/Toronto',
  'king city, ontario': 'America/Toronto',
  'toronto': 'America/Toronto',
  'ottawa': 'America/Toronto',
  'montreal': 'America/Montreal',
  'vancouver': 'America/Vancouver',
  'calgary': 'America/Edmonton',
  'edmonton': 'America/Edmonton',
  'winnipeg': 'America/Winnipeg',
  'halifax': 'America/Halifax',
  'st. john\'s': 'America/St_Johns',
  'new york': 'America/New_York',
  'los angeles': 'America/Los_Angeles',
  'chicago': 'America/Chicago',
  'london': 'Europe/London',
  'paris': 'Europe/Paris',
  'tokyo': 'Asia/Tokyo',
  'sydney': 'Australia/Sydney',
  'mumbai': 'Asia/Kolkata',
  'dubai': 'Asia/Dubai'
}

/** Resolve city name or IANA timezone to IANA timezone. Exported for clock widget. */
export function resolveTimezone(cityOrTimezone?: string): string {
  if (!cityOrTimezone || !cityOrTimezone.trim()) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }
  const key = cityOrTimezone.trim().toLowerCase()
  return CITY_TIMEZONE[key] ?? cityOrTimezone.trim()
}

async function getCurrentTime(
  params:
    | { parameters: { city?: string; timezone?: string } }
    | { city?: string; timezone?: string }
): Promise<CurrentTimeResult> {
  const start = Date.now()
  if ('parameters' in params) {
    params = params.parameters
  }
  const city = params.city?.trim()
  const tzParam = params.timezone?.trim()
  const timezone = tzParam || (city ? resolveTimezone(city) : undefined) || Intl.DateTimeFormat().resolvedOptions().timeZone

  const now = new Date()
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
  const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
  const tzAbbr = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    timeZoneName: 'short'
  })
    .formatToParts(now)
    .find((p) => p.type === 'timeZoneName')
    ?.value

  const time = formatter.format(now)
  const date = dateFormatter.format(now)
  const formatted = `${time} (${tzAbbr || timezone})`
  const responseTime = `${Date.now() - start}ms`

  return {
    time,
    date,
    timezone,
    ...(city && { city }),
    formatted,
    responseTime
  }
}

const getCurrentTimeFunction: ToolFunction = {
  id: 'getCurrentTime',
  description:
    'Get the current time. Use when the user asks for the time, or "what time is it", optionally for a city (e.g. "King City", "Toronto") or timezone (e.g. America/Toronto).',
  resultProcessingMode: 'send_to_llm',
  parameters: [
    {
      name: 'city',
      description:
        'Optional city name (e.g. "King City", "Toronto"). Used to show time in that location.',
      required: false,
      schema: { type: 'string' }
    },
    {
      name: 'timezone',
      description:
        'Optional IANA timezone (e.g. America/Toronto). Use if city is not provided or for precise timezone.',
      required: false,
      schema: { type: 'string' }
    }
  ],
  toolFunction: getCurrentTime,
  responseSchema: {
    type: 'object',
    properties: {
      time: { type: 'string', description: 'Current time in 12h format' },
      date: { type: 'string', description: 'Full date (weekday, month day, year)' },
      timezone: { type: 'string', description: 'IANA timezone used' },
      city: { type: 'string', description: 'City if requested' },
      formatted: { type: 'string', description: 'Time with timezone abbreviation' }
    }
  }
}

export const currentTimeTool: PlatformTool = {
  id: '00000000-0000-4000-8000-00000000000c',
  toolName: 'currentTime',
  name: 'Current Time',
  version: '1.0.0',
  description:
    'Returns the current time, optionally for a city or timezone. Use for "what time is it" or "time in King City".',
  toolsFunctions: [getCurrentTimeFunction]
}
